import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  InjectionToken,
  OnDestroy,
  Optional
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { MatCalendar, MatDatepickerIntl } from '@angular/material/datepicker';
import { LucideAngularModule } from 'lucide-angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface DatepickerCloseHandler {
  closeActivePicker(): void;
}

export const DATEPICKER_CLOSE_HANDLER = new InjectionToken<DatepickerCloseHandler>('DATEPICKER_CLOSE_HANDLER');

@Component({
  selector: 'app-datepicker-custom-header',
  standalone: true,
  imports: [CommonModule, MatButtonModule, LucideAngularModule],
  template: `
    <div class="mat-calendar-header datepicker-custom-header">
      <div class="mat-calendar-controls">
        <button
          mat-button
          type="button"
          class="mat-calendar-period-button"
          (click)="currentPeriodClicked()"
          [attr.aria-label]="periodButtonDescription">
          {{ periodButtonText }}
          <div class="mat-calendar-arrow" [class.mat-calendar-invert]="calendar.currentView !== 'month'"></div>
        </button>

        <div class="mat-calendar-spacer"></div>

        <button
          mat-icon-button
          type="button"
          class="mat-calendar-previous-button"
          [disabled]="!previousEnabled()"
          (click)="previousClicked()"
          aria-label="Mês anterior">
        </button>

        <button
          mat-icon-button
          type="button"
          class="mat-calendar-next-button"
          [disabled]="!nextEnabled()"
          (click)="nextClicked()"
          aria-label="Próximo mês">
        </button>

        <button
          mat-icon-button
          type="button"
          class="datepicker-close-btn"
          (click)="close()"
          aria-label="Fechar">
          <lucide-icon name="x" class="w-4 h-4"></lucide-icon>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .datepicker-custom-header .mat-calendar-controls {
      align-items: center;
    }
    .datepicker-close-btn {
      width: 36px;
      height: 36px;
      line-height: 36px;
      color: inherit;
      opacity: 0.7;
    }
    .datepicker-close-btn:hover {
      opacity: 1;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatepickerCustomHeaderComponent<D> implements OnDestroy {
  private readonly _destroyed = new Subject<void>();

  constructor(
    private readonly _intl: MatDatepickerIntl,
    public readonly calendar: MatCalendar<D>,
    private readonly _dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private readonly _dateFormats: MatDateFormats,
    cdr: ChangeDetectorRef,
    @Optional() @Inject(DATEPICKER_CLOSE_HANDLER) private readonly _closeHandler: DatepickerCloseHandler | null
  ) {
    calendar.stateChanges
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => cdr.markForCheck());
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }

  get periodButtonText(): string {
    if (this.calendar.currentView === 'month') {
      return this._dateAdapter
        .format(this.calendar.activeDate, this._dateFormats.display.monthYearLabel)
        .toLocaleUpperCase();
    }
    if (this.calendar.currentView === 'year') {
      return this._dateAdapter.getYearName(this.calendar.activeDate);
    }
    // multi-year: mostra range de anos
    const activeYear = this._dateAdapter.getYear(this.calendar.activeDate);
    const minYearOfPage = activeYear - (activeYear % 24);
    return `${minYearOfPage} \u2013 ${minYearOfPage + 23}`;
  }

  get periodButtonDescription(): string {
    return this._intl.switchToMultiYearViewLabel;
  }

  currentPeriodClicked(): void {
    this.calendar.currentView = this.calendar.currentView !== 'month' ? 'month' : 'multi-year';
  }

  previousClicked(): void {
    this.calendar.activeDate =
      this.calendar.currentView === 'month'
        ? this._dateAdapter.addCalendarMonths(this.calendar.activeDate, -1)
        : this._dateAdapter.addCalendarYears(this.calendar.activeDate, -1);
  }

  nextClicked(): void {
    this.calendar.activeDate =
      this.calendar.currentView === 'month'
        ? this._dateAdapter.addCalendarMonths(this.calendar.activeDate, 1)
        : this._dateAdapter.addCalendarYears(this.calendar.activeDate, 1);
  }

  previousEnabled(): boolean {
    if (!this.calendar.minDate) {
      return true;
    }
    return !this._isSameView(this.calendar.activeDate, this.calendar.minDate);
  }

  nextEnabled(): boolean {
    if (!this.calendar.maxDate) {
      return true;
    }
    return !this._isSameView(this.calendar.activeDate, this.calendar.maxDate);
  }

  close(): void {
    this._closeHandler?.closeActivePicker();
  }

  private _isSameView(date1: D, date2: D): boolean {
    if (this.calendar.currentView === 'month') {
      return (
        this._dateAdapter.getYear(date1) === this._dateAdapter.getYear(date2) &&
        this._dateAdapter.getMonth(date1) === this._dateAdapter.getMonth(date2)
      );
    }
    if (this.calendar.currentView === 'year') {
      return this._dateAdapter.getYear(date1) === this._dateAdapter.getYear(date2);
    }
    return Math.floor(this._dateAdapter.getYear(date1) / 24) === Math.floor(this._dateAdapter.getYear(date2) / 24);
  }
}
